// services/bookingService.ts
import { 
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  writeBatch,
  deleteDoc,
  Firestore,
  Timestamp
} from 'firebase/firestore';
import { db } from '@/firebase/config';
import { BookingRequest, BookingUpdate, BookingStatus } from '@/types/bookings';
import { Booking } from '@/types/calendar';
import { MessagingService } from './messagingService';
import { UserProfile } from '@/types/user';


export class BookingServiceError extends Error {
  constructor(
    message: string,
    public code: 'AUTH_REQUIRED' | 'UPDATE_FAILED' | 'FETCH_FAILED' | 'DELETE_FAILED' | 'MISSING_USER_ID' | 'INVALID_USER_TYPE' | 'UNAUTHORIZED',
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'BookingServiceError';
  }
}

export class BookingService {
  private db: Firestore;
  private messagingService: MessagingService;
  private static readonly CALENDAR_COLLECTION = 'bookings';
  private static readonly REQUESTS_COLLECTION = 'bookingRequests';

  constructor(db: Firestore) {
    this.db = db;
    this.messagingService = new MessagingService(db);
  }

  // Calendar Booking Methods (Original functionality)
  private getUserBookingsRef(userId: string) {
    return collection(this.db, `users/${userId}/bookings`);
  }

  async getUserBookings(userId: string): Promise<Booking[]> {
    try {
      const userDoc = await getDoc(doc(this.db, 'users', userId));
      
      if (!userDoc.exists()) {
        console.log('User document not found');
        return [];
      }

      const userData = userDoc.data() as UserProfile;
      
      if (userData.type === 'promoter') {
        const eventsRef = collection(this.db, `users/${userId}/events`);
        const eventsSnapshot = await getDocs(eventsRef);
        
        const mappedEvents = eventsSnapshot.docs.map(doc => {
          const eventData = doc.data();
          return {
            id: doc.id,
            talentId: userId,
            eventDate: eventData.eventDate?.toDate().toISOString().split('T')[0] || '',
            talentCallTime: eventData.startTime || '00:00',
            showStartTime: eventData.endTime || '23:59',
            eventName: eventData.eventName || 'Untitled Event',
            venueName: eventData.venueName,
            location: eventData.venueAddress || '',
            status: 'confirmed',
            type: 'event' as const,
            createdAt: eventData.createdAt || new Date(),
            updatedAt: eventData.updatedAt || new Date()
          } as Booking;
        });
        
        return mappedEvents;
      } else {
        const bookingsQuery = query(this.getUserBookingsRef(userId));
        const querySnapshot = await getDocs(bookingsQuery);
        
        
        if (querySnapshot.empty) {
          console.log('No bookings found for talent');
          return [];
        }

        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          type: 'booking'
        })) as Booking[];
      }
    } catch (error) {
      console.error('Error in getUserBookings:', error);
      if (error instanceof Error && 
          error.message.includes('collection does not exist')) {
        return [];
      }
      throw new BookingServiceError(
        'Failed to fetch bookings',
        'FETCH_FAILED',
        error
      );
    }
  }

  async addBooking(booking: Omit<Booking, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const { talentId, notes, venueName, ...restBooking } = booking;
      
      if (!talentId) {
        throw new BookingServiceError(
          'Talent ID is required',
          'MISSING_USER_ID'
        );
      }
      
      const newBookingRef = doc(this.getUserBookingsRef(talentId));
      
      const bookingData = {
        ...restBooking,
        talentId,
        ...(venueName && { venueName }),
        ...(notes && { notes }),
        status: 'confirmed',
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await setDoc(newBookingRef, bookingData);
      return newBookingRef.id;
    } catch (error) {
      throw new BookingServiceError(
        'Failed to add booking',
        'UPDATE_FAILED',
        error
      );
    }
  }

  async deleteBooking(userId: string, bookingId: string): Promise<void> {
    try {
      await deleteDoc(doc(this.db, `users/${userId}/bookings`, bookingId));
    } catch (error) {
      throw new BookingServiceError(
        'Failed to delete booking',
        'DELETE_FAILED',
        error
      );
    }
  }

  // Booking Request Methods (New functionality)
  async createBookingRequest(formData: FormData): Promise<BookingRequest> {
    const bookingRef = doc(collection(this.db, 'bookingRequests'));
    
    // Get the event date and ensure it's properly formatted
    const eventDate = formData.get('eventDate') as string;
    const parsedDate = new Date(eventDate);
    
    const bookingData: Partial<BookingRequest> = {
      id: bookingRef.id,
      talentId: formData.get('talentId') as string,
      promoterId: formData.get('promoterId') as string,
      conversationId: '', 
      promoterName: formData.get('promoterName') as string,
      promotionCompany: formData.get('promotionCompany') as string,
      eventName: formData.get('eventName') as string,
      // Store as Firestore timestamp
      eventDate: Timestamp.fromDate(parsedDate),
      location: formData.get('location') as string,
      talentCallTime: formData.get('talentCallTime') as string,
      showStartTime: formData.get('showStartTime') as string,
      travelExpenses: formData.get('travelExpenses') === 'true',
      status: 'pending',
      rateType: formData.get('rateType') as 'fixed' | 'discuss',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastActionBy: formData.get('promoterId') as string,
      lastActionAt: serverTimestamp()
    };

    // Add eventId if it exists
    const eventId = formData.get('eventId');
    if (eventId) {
      bookingData.eventId = eventId as string;
    }

    // Handle optional fields
    const optionalFields = {
      socialMedia: formData.get('socialMedia'),
      website: formData.get('website'),
      notes: formData.get('notes'),
    } as const;

    Object.entries(optionalFields).forEach(([key, value]) => {
      if (value && value.toString().trim() !== '') {
        bookingData[key as keyof typeof optionalFields] = value as string;
      }
    });

    // Handle fixedRate separately
    if (formData.get('rateType') === 'fixed') {
      const fixedRate = formData.get('fixedRate');
      if (fixedRate && !isNaN(Number(fixedRate))) {
        bookingData.fixedRate = Number(fixedRate);
      }
    }

    // Clean the object to remove any undefined values
    const cleanBookingData = {
      ...Object.fromEntries(
        Object.entries(bookingData).filter(([_, value]) => value !== undefined)
      )
    } as unknown as BookingRequest;

    await setDoc(bookingRef, cleanBookingData);
    return cleanBookingData;
  }

  async updateBookingRequest(
    bookingId: string,
    userId: string,
    updates: BookingUpdate
  ): Promise<void> {
    try {
      const bookingRef = doc(this.db, BookingService.REQUESTS_COLLECTION, bookingId);
      const bookingDoc = await getDoc(bookingRef);

      if (!bookingDoc.exists()) {
        throw new Error('Booking request not found');
      }

      const booking = bookingDoc.data() as BookingRequest;
      
      // Validate user has permission
      if (booking.talentId !== userId && booking.promoterId !== userId) {
        throw new BookingServiceError(
          'Unauthorized to update this booking',
          'UNAUTHORIZED'
        );
      }

      await updateDoc(bookingRef, {
        ...updates,
        updatedAt: serverTimestamp(),
        lastActionBy: userId,
        lastActionAt: serverTimestamp()
      });
    } catch (error) {
      throw new BookingServiceError(
        'Failed to update booking request',
        'UPDATE_FAILED',
        error
      );
    }
  }

  async getBookingRequests(userId: string): Promise<BookingRequest[]> {
    const q = query(
      collection(this.db, BookingService.REQUESTS_COLLECTION),
      where('promoterId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as BookingRequest[];
  }

  async isTalentBookedForEvent(
    promoterId: string,
    eventId: string,
    talentId: string
  ): Promise<boolean> {
    const eventBookingsRef = collection(
      this.db,
      `users/${promoterId}/events/${eventId}/bookings`
    );
    const q = query(eventBookingsRef, where("talentId", "==", talentId));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  }

  private getUpdateAction(status?: BookingStatus): 'updated' | 'accepted' | 'rejected' | 'cancelled' {
    switch (status) {
      case 'accepted':
        return 'accepted';
      case 'rejected':
        return 'rejected';
      case 'cancelled':
        return 'cancelled';
      default:
        return 'updated';
    }
  }
}

export default new BookingService(db);